import * as React from 'react';
import {NavLink} from 'react-router-dom';
import {Button} from '~/components/Button';
import {CheckboxInput} from '~/components/Controls/Checkbox';
import {TagsList} from '~/components/Controls/Tags';
import {useConfirm} from '~/components/Dialogs';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {useAbility} from '~/lib/ability';
import {IDataSource} from '~/lib/dataSource';
import {Copy} from '~/lib/helpers';
import {Intent} from '~/lib/intent';
import {notify} from '~/lib/notify';
import {sortByNumber, sortByString} from '~/lib/sort';
import {biddersLinks} from '~/modules/Bidders';
import {BiddersAbility} from '~/modules/Bidders/ability';
import {IBiddersFilter} from '~/modules/Bidders/api';
import {IBidderSummary} from '~/modules/Bidders/models/BidderSummary';
import {clientsLinks} from '~/modules/Clients';


const renderFloorValue = (value: number | string | null | undefined) =>
    value === null || value === undefined || value === '' ? '—' : value;

const LazyBidderRulesPickerModal = React.lazy(() =>
    import('~/modules/Bidders/forms/BidderRulesPickerModal')
        .then(module => ({default: module.BidderRulesPickerModal}))
);

const BidderRulesPickerModalLoader = (props: any) => (
    <React.Suspense fallback={<Progress/>}>
        <LazyBidderRulesPickerModal {...props}/>
    </React.Suspense>
);

interface IBiddersTable<T extends IBidderSummary = IBidderSummary> {
    dataSource: IDataSource<T>;
    onEdit?: (bidder: T) => void;
    onMutated?: () => void;
    resolveRulesConfigIds?: (bidder: T) => Promise<number[]>;
    serverFiltered?: boolean;
    disableClientSort?: boolean;
    showCheck?: boolean;
    filter?: IBiddersFilter;
    setFilter?: (filter) => any;
    isPending?: boolean;
}

const normalizeSearch = (value?: string) => (value ?? '').trim().toLowerCase();

const normalizeConfigIds = (values?: number[]) => {
    if (!Array.isArray(values)) {
        return [];
    }
    const unique = Array.from(new Set(values.filter((id): id is number => typeof id === 'number')));
    return unique.sort((a, b) => b - a);
};

const getMaxQps = (bidder: IBidderSummary) =>
    typeof bidder.maxQps === 'number' ? bidder.maxQps : bidder.maxRpm;

const getDatacenters = (bidder: IBidderSummary) =>
    bidder.datacenters ?? bidder.dc ?? [];

const formatAuctionCategory = (value?: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : '—';

const sortByMaxQps = <T extends IBidderSummary>(records: T[], _field: string, direction: TableSortDirection): T[] => {
    const missingValue = direction === TableSortDirection.ASC ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    const getValue = (record: T) => {
        const value = getMaxQps(record);
        return typeof value === 'number' ? value : missingValue;
    };
    return direction === TableSortDirection.ASC
        ? records.sort((a, b) => getValue(a) - getValue(b))
        : records.sort((a, b) => getValue(b) - getValue(a));
};

export const BiddersTable = <T extends IBidderSummary>(
    {
        dataSource,
        onEdit,
        onMutated,
        resolveRulesConfigIds,
        serverFiltered,
        disableClientSort,
        showCheck,
        filter,
        setFilter,
        isPending
    }: IBiddersTable<T>
) => {
    const ability = useAbility(BiddersAbility);
    const confirm = useConfirm();
    const modals = useModals();
    const rulesPickerModal = modals.create<any>(BidderRulesPickerModalLoader, {onMutated});
    const [rulesLoadingByBidderId, setRulesLoadingByBidderId] = React.useState<Record<string, boolean>>({});

    const setCheck = (source: IBidderSummary) => {
        let bulkCount = 0;
        source.check = !source.check;
        dataSource.state.records.map(field => {
            if (field.check) {
                bulkCount++;
            }
        });

        if (bulkCount > 1) {
            setFilter({...filter, bulkActions: true});
        } else {
            setFilter({...filter, bulkActions: false});
        }
    };

    const recordsFilter = (record: IBidderSummary) => {
        if (serverFiltered) {
            return true;
        }
        if (filter) {
            const bidderSearch = normalizeSearch(filter.bidderSearch);
            const agencySearch = normalizeSearch(filter.agencySearch);
            const tagSearch = normalizeSearch(filter.tag);
            const recordId = String(record.id ?? '').toLowerCase();
            const recordAgencyId = String(record.agencyId ?? '').toLowerCase();
            const recordTitle = (record.title ?? '').toLowerCase();
            const recordAgencyName = (record.agencyName ?? '').toLowerCase();
            const recordTags = (record.tags ?? []).map(tag => tag.toLowerCase());

            if (bidderSearch &&
                !recordTitle.includes(bidderSearch) &&
                !recordId.includes(bidderSearch)) {
                return;
            }
            if (agencySearch &&
                !recordAgencyName.includes(agencySearch) &&
                !recordAgencyId.includes(agencySearch)) {
                return;
            }
            if (tagSearch && !recordTags.includes(tagSearch)) {
                return;
            }
            if (filter.auctionCategory && record.auctionCategory !== filter.auctionCategory) {
                return;
            }
        }
        return record;
    };

    const records = Array.isArray(dataSource.state.records) ? dataSource.state.records : [];
    const filteredRecords = serverFiltered
        ? records
        : records.filter(recordsFilter);

    const setCheckAll = () => {
        let bulkCount = 0;
        filteredRecords.map((record) => {
            if (!filter.checkAll && !record.check) {
                record.check = true;
            } else if (filter.checkAll && record.check) {
                record.check = false;
            }
        });
        dataSource.state.records.map(field => {
            if (field.check) {
                bulkCount++;
            }
        });
        if (bulkCount > 1) {
            setFilter({...filter, bulkActions: true, checkAll: !filter.checkAll});
        } else {
            setFilter({...filter, bulkActions: false, checkAll: !filter.checkAll});
        }
    };

    const openRulesModal = (bidder: T, configIds: number[]) => {
        rulesPickerModal.show({
            bidderTitle: bidder.title,
            bidderId: bidder.id,
            configIds
        }, {style: {maxWidth: 580}});
    };

    const loadRulesConfigIds = async (bidder: T) => {
        if (!resolveRulesConfigIds) {
            return;
        }
        const bidderIdKey = String(bidder.id ?? '');
        setRulesLoadingByBidderId(prev => ({...prev, [bidderIdKey]: true}));
        try {
            const resolvedIds = normalizeConfigIds(await resolveRulesConfigIds(bidder));
            if (!resolvedIds.length) {
                notify.warning('No rules configs for this bidder.');
                return;
            }
            openRulesModal(bidder, resolvedIds);
        } catch (error) {
            notify.apiError(error, 'Failed to load bidder rules.');
        } finally {
            setRulesLoadingByBidderId(prev => ({...prev, [bidderIdKey]: false}));
        }
    };

    return <TableSort records={filteredRecords}
        defaultSort={disableClientSort ? undefined : {
            sort: sortByNumber,
            field: 'id',
            direction: TableSortDirection.DESC
        }}
        header
        columns={[
            {
                field: 'check',
                header: (showCheck && filter ?
                    <CheckboxInput
                        value={filter.checkAll}
                        onChange={() => setCheckAll()}
                        className='placeholder'
                        variant='primary'
                    /> : ''),
                small: true,
                hidden: !showCheck,
                cell: bidder => <CheckboxInput value={bidder.check} onChange={() => setCheck(bidder)}
                    className='placeholder'/>
            }, {
                field: 'id',
                header: 'Id',
                sort: disableClientSort ? undefined : sortByNumber,
                small: true
            }, {
                field: 'title',
                header: 'Title',
                sort: disableClientSort ? undefined : sortByString,
                headerAttrs: { style: { minWidth: 400 } },
                cellAttrs: { style: { minWidth: 400 } },
                cellWrap: 'break-word',
                cell: bidder => <NavLink to={biddersLinks.bidder(bidder.id)}>{bidder.title}</NavLink>
            }, {
                field: 'agencyId',
                header: 'Agency',
                responsive: true,
                cell: bidder => {
                    const agencyId = bidder.agencyId ?? '—';
                    const label = bidder.agencyName ? `${agencyId} · ${bidder.agencyName}` : agencyId;
                    return (
                        <NavLink to={clientsLinks.agency(bidder.agencyId)}>
                            {label}
                        </NavLink>
                    );
                }
            }, {
                field: 'tags',
                header: 'Tags',
                cell: bidder => <TagsList values={bidder.tags ?? []} size="small"/>,
                short: true
            }, {
                field: 'endpoint',
                header: 'Endpoint',
                sort: disableClientSort ? undefined : sortByString,
                cellAttrs: {
                    style: {
                        maxWidth: 0
                    }
                },
                responsive: true,
                cell: bidder => <Copy title={bidder.endpoint}
                    text={bidder.endpoint}>{bidder.endpoint}</Copy>
            }, {
                field: 'datacenters',
                header: 'Datacenters',
                headerAttrs: { style: { minWidth: 180 } },
                cellAttrs: { style: { minWidth: 180 } },
                cell: bidder => <TagsList values={getDatacenters(bidder)} size="small"/>,
                short: true
            }, 
            {
                field: "auctionCategory",
                header: "Auction Category",
                responsive: true,
                sort: disableClientSort ? undefined : sortByString,
                cell: bidder => formatAuctionCategory(bidder.auctionCategory)
            }, {
                field: 'protocol',
                header: 'Protocol',
                sort: disableClientSort ? undefined : sortByString,
                responsive: true
            }, {
                field: 'minBidFloor',
                header: 'Min/Max Floor',
                sort: disableClientSort ? undefined : sortByNumber,
                responsive: true,
                cell: bidder => <span>
                    {renderFloorValue(bidder.minBidFloor)} / {renderFloorValue(bidder.maxBidFloor)}
                </span>
            }, {
                field: 'maxQps',
                responsive: true,
                header: 'Max QPS',
                sort: disableClientSort ? undefined : sortByMaxQps,
                cell: bidder => renderFloorValue(getMaxQps(bidder))
            }, {
                header: '',
                short: true,
                hide: !ability.canEdit(),
                cell: bidder => {
                    const configIds = normalizeConfigIds(bidder.configIds);
                    const hasResolver = typeof resolveRulesConfigIds === 'function';
                    if (!configIds.length && !hasResolver) {
                        return '';
                    }
                    const bidderIdKey = String(bidder.id ?? '');
                    const isRulesLoading = !!rulesLoadingByBidderId[bidderIdKey];
                    return (
                        <Button
                            small
                            responsive
                            intent={Intent.WARNING}
                            disabled={isRulesLoading}
                            rightIcon={isRulesLoading ? <i className='fa fa-spinner fa-spin'/> : undefined}
                            onClick={() => {
                                if (configIds.length) {
                                    openRulesModal(bidder, configIds);
                                    return;
                                }
                                loadRulesConfigIds(bidder);
                            }}
                            leftIcon={<i className="fa fa-codepen" />}
                        >
                            Rules
                        </Button>
                    );
                }
            }, {
                cell: bidder => <TableRowActions>
                    {ability.canCopy() && <Button
                        small
                        variant="menu"
                        onClick={() => confirm('Clone bidder?', {
                            onOk: () => dataSource.copyRecord(bidder.id)
                                .then(() => {
                                    notify.success('Bidder cloned successfully');
                                    onMutated?.();
                                })
                                .catch((error) => {
                                    notify.apiError(error, 'Failed to clone bidder');
                                }),
                            intent: Intent.PRIMARY
                        })}
                        leftIcon={<i className='far fa-copy'/>}
                    >Clone</Button>}
                    {ability.canEdit() && onEdit && <Button
                        small
                        variant="menu"
                        onClick={() => onEdit(bidder)}
                        leftIcon={<i className='fa fa-edit'/>}
                    >Edit</Button>}
                    {ability.canDelete() && <Button
                        small
                        variant="menu"
                        intent={Intent.DANGER}
                        onClick={() => confirm('Delete bidder?', {
                            onOk: () => dataSource.deleteRecord(bidder)
                                .then(() => {
                                    notify.success('Bidder deleted successfully');
                                    onMutated?.();
                                })
                                .catch((error) => {
                                    notify.apiError(error, 'Failed to delete bidder');
                                }),
                            intent: Intent.DANGER
                        })}
                        leftIcon={<i className='fa fa-trash'/>}
                    >Delete</Button>}
                </TableRowActions>,
                small: true
            }
        ]}
        placeholder={(isPending || dataSource.state.isPending) && <Progress/>}
    />;
};
